'use client';

import { useState, useMemo, useRef, useEffect, useCallback, forwardRef } from 'react';
import useSWR from 'swr';
import { getCountries, getCountryCallingCode, parsePhoneNumberFromString, AsYouType, type CountryCode } from 'libphonenumber-js';
import { useTranslation } from '../lib/i18n';
import { useNationalEnvironment } from '../lib/useNationalEnvironment';
import { validateAndNormalizePhone } from '../lib/phone';

const DIAL_RE = /^\+(\d{1,4})/;
/** Value that is only a dialing prefix (e.g. "+420") = effectively empty. */
const BARE_PREFIX_RE = /^\+\d{1,4}$/;

export function phoneErrorKey(value: string, country?: string): string | null {
  const trimmed = (value || '').trim();
  if (!trimmed || BARE_PREFIX_RE.test(trimmed)) return null;
  const result = validateAndNormalizePhone(trimmed, country);
  if (result.valid) return null;
  return result.error === 'invalidFormat' ? 'phoneInvalidFormat' : 'phoneInvalidNumber';
}

export function normalizePhone(value: string, country?: string): string {
  const trimmed = (value || '').trim();
  if (!trimmed || BARE_PREFIX_RE.test(trimmed)) return '';
  const result = validateAndNormalizePhone(trimmed, country);
  return result.valid ? (result.normalized || value) : value;
}

/** ISO code → flag emoji (regional indicators). On Windows renders as letters — still readable. */
export function countryFlag(code: string): string {
  if (!/^[A-Z]{2}$/.test(code)) return '';
  return String.fromCodePoint(code.charCodeAt(0) + 127397, code.charCodeAt(1) + 127397);
}

/** Country code currently implied by the value (from a "+" prefix), or undefined. */
function detectedCountry(value: string): CountryCode | undefined {
  const trimmed = (value || '').trim();
  if (!trimmed.startsWith('+')) return undefined;
  const asYouType = new AsYouType();
  asYouType.input(trimmed);
  return asYouType.getCountry() ?? parsePhoneNumberFromString(trimmed)?.country;
}

type PhoneInputProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type' | 'onChange'> & {
  label?: string;
  showLabel?: boolean;
  /** ISO 3166-1 alpha-2 default region for numbers without a country prefix. Defaults to the user's national environment. */
  country?: string;
  /** When false, only validates — never rewrites the value on blur. Default true. */
  normalizeOnBlur?: boolean;
  /** When false, hides the country-prefix chip (plain input only). Default true. */
  showCountrySelect?: boolean;
  /** When true, validation error shows even before blur (e.g. on submit attempt). */
  forceError?: boolean;
  /** Wrapper className for the label+input+error block. */
  wrapperClassName?: string;
  /** Receives the (possibly normalized) string value. */
  onChange?: (value: string) => void;
};

const FREQ_KEY = 'armorik:phone-country-freq';
const FREQ_LIMIT = 8;

function readFreq(): Record<string, number> {
  try { return JSON.parse(localStorage.getItem(FREQ_KEY) || '{}'); } catch { return {}; }
}
function bumpFreq(code: string) {
  try {
    const freq = readFreq();
    freq[code] = (freq[code] || 0) + 1;
    localStorage.setItem(FREQ_KEY, JSON.stringify(freq));
  } catch { /* storage unavailable */ }
}

const PhoneInput = forwardRef<HTMLInputElement, PhoneInputProps>(function PhoneInput(
  { label, showLabel = true, country, normalizeOnBlur = true, showCountrySelect = true, forceError = false, wrapperClassName = '', className = '', value, onChange, onBlur, required, disabled, ...rest },
  ref
) {
  const { t, locale } = useTranslation();
  const { country: envCountry } = useNationalEnvironment();
  const [touched, setTouched] = useState(false);
  const [picked, setPicked] = useState<CountryCode | undefined>(undefined);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [freq, setFreq] = useState<Record<string, number>>({});
  const wrapRef = useRef<HTMLDivElement>(null);

  const strValue = typeof value === 'string' ? value : '';
  const detected = detectedCountry(strValue);
  const effectiveCountry = (detected || picked || country || envCountry || 'CZ').toUpperCase() as CountryCode;

  const errorKey = phoneErrorKey(strValue, effectiveCountry);
  const showError = (touched || forceError) && errorKey !== null;

  // Admin-defined country order (Admin → Pořadí číselníků → Adresní země).
  // Fetched only for authenticated users — public pages (checkout, booking) fall back to alphabetical.
  const { data: adminCountries } = useSWR<{ code: string }[]>(
    typeof window !== 'undefined' && window.localStorage.getItem('userId') ? '/api/user/obchod/accounts/countries' : null,
  );
  const adminOrderIdx = useMemo(() => {
    const m = new Map<string, number>();
    (adminCountries || []).forEach((c, i) => m.set((c.code || '').toUpperCase(), i));
    return m;
  }, [adminCountries]);

  // Country list — localized names via Intl.DisplayNames, dial codes from libphonenumber.
  const countries = useMemo(() => {
    let names: Intl.DisplayNames | null = null;
    try { names = new Intl.DisplayNames([locale || 'cs'], { type: 'region' }); } catch { names = null; }
    const list = getCountries()
      .map((code) => ({
        code,
        dial: `+${getCountryCallingCode(code)}`,
        name: names?.of(code) || code,
      }))
      .sort((a, b) => a.name.localeCompare(b.name, locale || 'cs'));
    // Pinned: aktuální země čísla → systémová země → četnost použití (localStorage).
    const sysCountry = (envCountry || 'CZ').toUpperCase();
    const topCodes = new Set([effectiveCountry, sysCountry]);
    const pinned = [effectiveCountry, sysCountry]
      .map((code) => list.find((c) => c.code === code))
      .filter((c): c is NonNullable<typeof c> => !!c)
      .filter((c, i, arr) => arr.findIndex((x) => x.code === c.code) === i);
    const frequent = Object.keys(freq)
      .filter((code) => (freq[code] || 0) > 0 && !topCodes.has(code))
      .sort((a, b) => (freq[b] || 0) - (freq[a] || 0))
      .slice(0, FREQ_LIMIT)
      .map((code) => list.find((c) => c.code === code))
      .filter((c): c is NonNullable<typeof c> => !!c);
    const shown = new Set([...pinned, ...frequent].map((c) => c.code));
    // "Všechny země" — admin pořadí (Pořadí číselníků) má přednost, zbytek abecedně.
    const all = list
      .filter((c) => !shown.has(c.code))
      .sort((a, b) => {
        const ai = adminOrderIdx.get(a.code) ?? Number.MAX_SAFE_INTEGER;
        const bi = adminOrderIdx.get(b.code) ?? Number.MAX_SAFE_INTEGER;
        return ai !== bi ? ai - bi : a.name.localeCompare(b.name, locale || 'cs');
      });
    return { pinned, frequent, all };
  }, [locale, effectiveCountry, envCountry, freq, adminOrderIdx]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return countries;
    const match = (c: { code: string; dial: string; name: string }) =>
      c.name.toLowerCase().includes(q) || c.dial.includes(q) || c.code.toLowerCase() === q;
    return {
      pinned: countries.pinned.filter(match),
      frequent: countries.frequent.filter(match),
      all: countries.all.filter(match),
    };
  }, [countries, query]);

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => { if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', close); document.removeEventListener('keydown', esc); };
  }, [open]);

  const pickCountry = useCallback((code: CountryCode) => {
    setPicked(code);
    setOpen(false);
    setQuery('');
    bumpFreq(code);
    setFreq(readFreq());
    if (!onChange) return;
    const dial = `+${getCountryCallingCode(code)}`;
    const trimmed = strValue.trim();
    if (!trimmed || BARE_PREFIX_RE.test(trimmed)) { onChange(`${dial} `); return; }
    if (trimmed.startsWith('+')) {
      // Replace the existing dialing prefix, keep the national part.
      const national = trimmed.replace(DIAL_RE, '').trim();
      onChange(national ? `${dial} ${national}` : `${dial} `);
      return;
    }
    onChange(`${dial} ${trimmed}`);
  }, [onChange, strValue]);

  const handleBlur = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    setTouched(true);
    if (normalizeOnBlur && onChange) {
      const normalized = normalizePhone(e.target.value, effectiveCountry);
      if (normalized !== e.target.value) onChange(normalized);
    }
    onBlur?.(e);
  }, [normalizeOnBlur, onChange, onBlur, effectiveCountry]);

  const chip = showCountrySelect ? (
    <button
      type="button"
      tabIndex={-1}
      disabled={disabled}
      onClick={() => setOpen((o) => { if (!o) setFreq(readFreq()); return !o; })}
      className="flex items-center gap-1 px-2 rounded-l border border-wheat bg-linen/60 text-sm text-bark shrink-0 hover:bg-wheat/40 disabled:opacity-50"
      title={t('phoneCountry')}
    >
      <span>{countryFlag(effectiveCountry)}</span>
      <span className="text-wood">+{getCountryCallingCode(effectiveCountry)}</span>
      <span className="text-[9px] text-wood">▾</span>
    </button>
  ) : null;

  const dropdown = open && (
    <div className="absolute left-0 top-full mt-1 z-50 w-72 max-h-64 overflow-hidden rounded border border-wheat bg-linen shadow-lg flex flex-col">
      <input
        autoFocus
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={t('phoneCountrySearch')}
        className="m-2 mb-1 px-2 py-1.5 text-sm border border-wheat rounded bg-cream text-bark outline-none"
      />
      <div className="overflow-y-auto">
        {filtered.pinned.map((c) => (
          <button key={c.code} type="button" onClick={() => pickCountry(c.code)}
            className={`w-full flex items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-wheat/30 ${c.code === effectiveCountry ? 'bg-wheat/20 font-medium' : ''}`}>
            <span className="w-5 text-center">{countryFlag(c.code)}</span>
            <span className="flex-1 truncate text-bark">{c.name}</span>
            <span className="text-wood text-xs">{c.dial}</span>
          </button>
        ))}
        {filtered.frequent.length > 0 && (
          <>
            <div className="px-3 pt-1.5 pb-0.5 text-[10px] font-semibold uppercase tracking-wide text-wood border-t border-wheat/50">{t('phoneCountryFrequent')}</div>
            {filtered.frequent.map((c) => (
              <button key={c.code} type="button" onClick={() => pickCountry(c.code)}
                className={`w-full flex items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-wheat/30 ${c.code === effectiveCountry ? 'bg-wheat/20 font-medium' : ''}`}>
                <span className="w-5 text-center">{countryFlag(c.code)}</span>
                <span className="flex-1 truncate text-bark">{c.name}</span>
                <span className="text-wood text-xs">{c.dial}</span>
              </button>
            ))}
          </>
        )}
        {filtered.all.length > 0 && (
          <div className="px-3 pt-1.5 pb-0.5 text-[10px] font-semibold uppercase tracking-wide text-wood border-t border-wheat/50">{t('phoneCountryAll')}</div>
        )}
        {filtered.all.map((c) => (
          <button key={c.code} type="button" onClick={() => pickCountry(c.code)}
            className={`w-full flex items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-wheat/30 ${c.code === effectiveCountry ? 'bg-wheat/20 font-medium' : ''}`}>
            <span className="w-5 text-center">{countryFlag(c.code)}</span>
            <span className="flex-1 truncate text-bark">{c.name}</span>
            <span className="text-wood text-xs">{c.dial}</span>
          </button>
        ))}
        {filtered.pinned.length + filtered.frequent.length + filtered.all.length === 0 && (
          <div className="px-3 py-2 text-xs text-wood">{t('phoneCountryNone')}</div>
        )}
      </div>
    </div>
  );

  return (
    <div className={wrapperClassName}>
      {showLabel && label && <label className="field-label">{label}{required && <span className="text-rust"> *</span>}</label>}
      <div ref={wrapRef} className="relative">
        <div className="flex items-stretch">
          {chip}
          <input
            ref={ref}
            type="tel"
            className={`${className} flex-1 min-w-0 ${showError ? 'field-input-invalid' : ''} ${chip ? 'rounded-l-none border-l-0' : ''}`}
            value={strValue}
            onChange={(e) => onChange?.(e.target.value)}
            onBlur={handleBlur}
            required={required}
            disabled={disabled}
            {...rest}
          />
        </div>
        {dropdown}
      </div>
      {showError && <p className="field-error-text">{t(errorKey)}</p>}
    </div>
  );
});

export default PhoneInput;
